
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { usePermissions } from "@/hooks/usePermissions";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SubscribersList } from "@/components/newsletter/SubscribersList";
import { CampaignsList } from "@/components/newsletter/CampaignsList";
import { NewsletterAnalytics } from "@/components/newsletter/NewsletterAnalytics";
import { TemplateManagement } from "@/components/newsletter/TemplateManagement";

export default function NewsletterManagement() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState("subscribers");
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse the tab from URL search params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    
    // Set the active tab based on the URL parameter, defaulting to "subscribers"
    if (tab && ['subscribers', 'campaigns', 'templates', 'analytics'].includes(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab("subscribers");
    }
  }, [location.search]);
  
  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    
    if (value === "subscribers") {
      // For the default tab, we don't need to include it in the URL
      navigate('/dashboard/newsletter', { replace: true });
    } else {
      navigate(`/dashboard/newsletter?tab=${value}`, { replace: true });
    }
  };
  
  // Redirect if user doesn't have permission
  if (!hasPermission("manage:newsletters") && !hasPermission("view:newsletters")) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-slate-800">Newsletter Management</h1>
        <p className="text-slate-600">
          Create, send, and analyze newsletter campaigns to your subscribers.
        </p>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="flex flex-col">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-800">Newsletter Management</h2>
              <p className="text-sm text-slate-500">
                Manage your newsletter subscribers, campaigns, and analytics
              </p>
            </div>
            
            <Tabs 
              value={activeTab} 
              onValueChange={handleTabChange} 
              className="w-full"
            >
              <div className="px-6 py-2 bg-slate-50 border-b border-slate-200">
                <TabsList className="grid w-full grid-cols-4 bg-slate-100">
                  <TabsTrigger 
                    value="subscribers" 
                    className="data-[state=active]:bg-white data-[state=active]:text-slate-800 data-[state=active]:shadow-none"
                  >
                    Subscribers
                  </TabsTrigger>
                  <TabsTrigger 
                    value="templates"
                    className="data-[state=active]:bg-white data-[state=active]:text-slate-800 data-[state=active]:shadow-none"
                  >
                    Templates
                  </TabsTrigger>
                  <TabsTrigger 
                    value="campaigns"
                    className="data-[state=active]:bg-white data-[state=active]:text-slate-800 data-[state=active]:shadow-none"
                  >
                    Campaigns
                  </TabsTrigger>
                  <TabsTrigger 
                    value="analytics"
                    className="data-[state=active]:bg-white data-[state=active]:text-slate-800 data-[state=active]:shadow-none"
                  >
                    Analytics
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="p-6">
                <TabsContent value="subscribers" className="mt-0">
                  <SubscribersList />
                </TabsContent>
                <TabsContent value="templates" className="mt-0">
                  <TemplateManagement />
                </TabsContent>
                <TabsContent value="campaigns" className="mt-0">
                  <CampaignsList />
                </TabsContent>
                <TabsContent value="analytics" className="mt-0">
                  <NewsletterAnalytics />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
